import type {
  ScheduleChatMessageSendingJobInputTextMessage,
  ScheduleChatMessageSendingJobInputImageMessage,
  ScheduleChatMessageSendingJobInputMessage,
} from '@/types/schema.generated'
import { useIssueChatMessageImageUploadUrlMutation } from './inputMessageParse.generated'
import type { ChatMessageSendingJobForm_Message } from './types'
import * as v from 'valibot'

export function useParseInputMessage() {
  const parseTextMessage = (
    message: ChatMessageSendingJobForm_Message
  ): ScheduleChatMessageSendingJobInputTextMessage => {
    const schema = v.object({
      text: v.pipe(v.string(), v.trim(), v.nonEmpty('メッセージが空です')),
      selections: v.optional(
        v.pipe(
          v.array(
            v.pipe(v.string(), v.trim(), v.nonEmpty('選択肢の内容が空です'))
          ),
          v.minLength(2, '選択肢は2つ以上必要です')
        )
      ),
    })

    const result = v.safeParse(schema, message)
    if (!result.success) {
      console.error(result)
      throw new Error(result.issues[0].message)
    }

    return {
      text: result.output.text,
      selections: result.output.selections ?? [],
    }
  }

  const { uploadImage } = useUploadImage()

  // LINE messaging API の imagemap の linkURI で許可されているスキーム
  // ref: https://developers.line.biz/ja/reference/messaging-api/#imagemap-uri-action-object
  const allowedUrlSchemes = ['http', 'https', 'tel']

  const parseImageMessage = async (
    message: ChatMessageSendingJobForm_Message
  ): Promise<ScheduleChatMessageSendingJobInputImageMessage> => {
    const schema = v.object({
      image: v.file('画像が空です'),
      linkUri: v.pipe(
        v.string(),
        v.trim(),
        v.nonEmpty('リンクURLを入力してください'),
        v.url('リンクURLが不正です'),
        v.check(
          input => allowedUrlSchemes.some(key => input.startsWith(key)),
          `リンクURLのプレフィックスは次のいずれかである必要があります: ${allowedUrlSchemes}`
        )
      ),
      altText: v.pipe(
        v.string(),
        v.trim(),
        v.nonEmpty('代替テキストを入力してください')
      ),
    })

    const parsedResult = v.safeParse(schema, message)
    if (!parsedResult.success) {
      console.error(parsedResult)
      throw new Error(parsedResult.issues[0].message)
    }

    const imageFileName = await uploadImage(parsedResult.output.image)

    return {
      fileName: imageFileName,
      altText: parsedResult.output.altText,
      linkUri: parsedResult.output.linkUri,
    }
  }

  const parseInputMessages = async (
    inputMessages: ChatMessageSendingJobForm_Message[]
  ): Promise<ScheduleChatMessageSendingJobInputMessage[]> => {
    return await Promise.all(
      inputMessages.map(async message => {
        if (message.type === 'image') {
          return {
            imageMessage: await parseImageMessage(message),
          }
        } else {
          return {
            textMessage: parseTextMessage(message),
          }
        }
      })
    )
  }

  return parseInputMessages
}

const useUploadImage = () => {
  const issueChatMessageImageUploadUrlMutation =
    useIssueChatMessageImageUploadUrlMutation()

  const uploadImage = async (file: File) => {
    const fileExtension = (() => {
      const array = file.name.split('.')
      return array[array.length - 1]
    })()

    const res = await issueChatMessageImageUploadUrlMutation.mutate({
      input: {
        fileExtension: fileExtension,
      },
    })
    if (!res) {
      throw new Error('failed to get res')
    }
    if (res.errors || res.data == undefined) {
      console.error(res.errors)
      throw new Error('failed to get res.data')
    }

    await fetch(res.data?.issueChatMessageImageUploadUrl.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    })

    return res.data.issueChatMessageImageUploadUrl.fileName
  }

  return {
    uploadImage,
  }
}
